import { traceOakStream } from "./trace";

export function parseStreamEventData(rawData: string) {
  try {
    return JSON.parse(rawData);
  } catch {
    return rawData;
  }
}

export interface ParsedSseFrame {
  id: string | undefined;
  event: string;
  rawData: string;
}

export interface StreamEvent {
  id: string;
  event: string;
  data: unknown;
}

interface StreamEventsOptions {
  url: string;
  getIdTokenRefreshIfExpired: () => Promise<string>;
  lastEventId?: string;
  signal: AbortSignal;
}

export function parseSseFrame(frame: string) {
  const lines = frame.split(/\r?\n/);
  let id: string | undefined;
  let event = "message";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (!line || line.startsWith(":")) {
      continue;
    }

    if (line.startsWith("id:")) {
      id = line.slice("id:".length).trimStart();
      continue;
    }

    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trimStart();
      continue;
    }

    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trimStart());
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return {
    id,
    event,
    rawData: dataLines.join("\n"),
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function toStreamEvent(frame: string): StreamEvent | null {
  const parsedFrame = parseSseFrame(frame);

  if (!parsedFrame?.id) {
    return null;
  }

  return {
    id: parsedFrame.id,
    event: parsedFrame.event,
    data: parseStreamEventData(parsedFrame.rawData),
  };
}

/**
 * Splits the accumulated text buffer into fully terminated SSE frames and one trailing
 * partial frame. Network reads from `ReadableStream` are arbitrary chunks, so a single
 * read can end mid-frame or contain multiple complete frames plus the start of the next.
 * Completed frames can be parsed immediately, while the pending trailing frame must stay
 * buffered and be prefixed to the next chunk before parsing.
 */
function getCompletedFrames(bufferedChunk: string): {
  completedFrames: string[];
  pendingFrame: string;
} {
  const frames = bufferedChunk.split(/\r?\n\r?\n/);

  return {
    completedFrames: frames.slice(0, -1),
    pendingFrame: frames.at(-1) ?? "",
  };
}

class ServerSideEventsIterator implements AsyncIterable<StreamEvent> {
  private bufferedChunk = "";
  private readonly bufferedEvents: StreamEvent[] = [];
  private didReleaseReader = false;
  private isDone = false;
  private readonly signal: AbortSignal;
  private readonly reader: ReadableStreamDefaultReader<Uint8Array>;
  private readonly decoder: TextDecoder;

  constructor({
    signal,
    reader,
    decoder,
  }: {
    signal: AbortSignal;
    decoder: TextDecoder;
    reader: ReadableStreamDefaultReader<Uint8Array>;
  }) {
    this.signal = signal;
    this.reader = reader;
    this.decoder = decoder;
    // Aborting the owning stream should cancel the underlying response body read
    // so any pending `reader.read()` resolves and this iterator can unwind.
    this.signal.addEventListener("abort", this.cancelReader, { once: true });
  }

  [Symbol.asyncIterator](): AsyncIterator<StreamEvent> {
    return {
      next: async (): Promise<IteratorResult<StreamEvent>> => {
        if (this.isDone) {
          return {
            done: true,
            value: undefined,
          };
        }

        try {
          while (!this.signal.aborted) {
            const nextBufferedEvent = this.bufferedEvents.shift();

            if (nextBufferedEvent) {
              return {
                done: false,
                value: nextBufferedEvent,
              };
            }

            // `read()` yields the next byte chunk from the HTTP response body.
            // `done: true` means the body itself has ended: the server closed the
            // SSE connection, the stream finished normally, or the reader was canceled.
            const { done, value } = await this.reader.read();

            if (done) {
              traceOakStream("iterator", "readerDone", {
                aborted: this.signal.aborted,
              });
              // Flush any decoder state plus the final partial frame before ending
              // iteration. `done` refers to the whole byte stream, not an SSE frame.
              const trailingChunk = `${this.bufferedChunk}${this.decoder.decode()}`.trim();

              if (trailingChunk) {
                this.enqueueFrame(trailingChunk);
              }

              this.finish();

              const trailingEvent = this.bufferedEvents.shift();
              if (trailingEvent) {
                return {
                  done: false,
                  value: trailingEvent,
                };
              }

              return {
                done: true,
                value: undefined,
              };
            }

            // Network reads are arbitrary byte chunks, so one read may contain part
            // of a frame, a full frame, or many frames. Keep the trailing partial
            // frame buffered for the next read.
            this.bufferedChunk += this.decoder.decode(value, { stream: true });
            const { completedFrames, pendingFrame } = getCompletedFrames(this.bufferedChunk);
            this.bufferedChunk = pendingFrame;

            for (const frame of completedFrames) {
              this.enqueueFrame(frame);
            }
          }

          this.finish();
          return {
            done: true,
            value: undefined,
          };
        } catch (error) {
          if (this.signal.aborted || isAbortError(error)) {
            // Cancellation is an expected shutdown path for long-lived streams, so
            // surface it as normal iterator completion instead of an error.
            this.finish();
            return {
              done: true,
              value: undefined,
            };
          }

          this.finish();
          throw error;
        }
      },
      return: async (): Promise<IteratorResult<StreamEvent>> => {
        this.cancelReader();
        this.finish();

        return {
          done: true,
          value: undefined,
        };
      },
    };
  }

  private enqueueFrame(frame: string) {
    const streamEvent = toStreamEvent(frame);

    if (streamEvent) {
      traceOakStream("iterator", "frame", {
        eventId: streamEvent.id,
        eventName: streamEvent.event,
      });
      this.bufferedEvents.push(streamEvent);
    }
  }

  private readonly cancelReader = () => {
    traceOakStream("iterator", "cancelReader", {
      aborted: this.signal.aborted,
    });
    void this.reader.cancel().catch(() => undefined);
  };

  private releaseReader() {
    if (this.didReleaseReader) {
      return;
    }

    this.didReleaseReader = true;
    this.reader.releaseLock();
  }

  private finish() {
    this.isDone = true;
    this.signal.removeEventListener("abort", this.cancelReader);
    this.releaseReader();
  }
}

function getSseBaseUrl() {
  return (
    import.meta.env.VITE_SSE_API_URL ||
    import.meta.env.VITE_API_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:4000" : "")
  );
}

export async function streamSideSideEventsIterator(
  options: StreamEventsOptions
): Promise<AsyncIterable<StreamEvent>> {
  const idToken = await options.getIdTokenRefreshIfExpired();
  const headers = new Headers({
    accept: "text/event-stream",
    authorization: `Bearer ${idToken}`,
  });

  const { lastEventId, url } = options;

  if (lastEventId) {
    headers.set("Last-Event-ID", lastEventId);
  }

  traceOakStream("iterator", "fetchStart", {
    url: `${getSseBaseUrl()}${url}`,
    lastEventId,
  });
  const response = await fetch(`${getSseBaseUrl()}${url}`, {
    headers,
    signal: options.signal,
  });

  traceOakStream("iterator", "fetchResponse", {
    url: `${getSseBaseUrl()}${url}`,
    ok: response.ok,
    status: response.status,
  });

  if (!response.ok) {
    throw new Error(`Failed to connect to SSE stream (${response.status}).`);
  }

  if (!response.body) {
    throw new Error("SSE response body was empty.");
  }

  return new ServerSideEventsIterator({
    signal: options.signal,
    decoder: new TextDecoder(),
    reader: response.body.getReader(),
  });
}

export async function* streamEventsGenerator(
  options: StreamEventsOptions
): AsyncGenerator<StreamEvent, void, void> {
  const idToken = await options.getIdTokenRefreshIfExpired();
  const headers = new Headers({
    accept: "text/event-stream",
    authorization: `Bearer ${idToken}`,
  });

  if (options.lastEventId) {
    headers.set("Last-Event-ID", options.lastEventId);
  }

  const response = await fetch(`${getSseBaseUrl()}${options.url}`, {
    headers,
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to connect to SSE stream (${response.status}).`);
  }

  if (!response.body) {
    throw new Error("SSE response body was empty.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const cancelReader = () => {
    void reader.cancel().catch(() => undefined);
  };

  options.signal.addEventListener("abort", cancelReader, { once: true });

  try {
    let bufferedChunk = "";

    while (!options.signal.aborted) {
      const { done, value } = await reader.read();

      if (done) {
        const trailingChunk = `${bufferedChunk}${decoder.decode()}`.trim();

        if (trailingChunk) {
          const trailingEvent = toStreamEvent(trailingChunk);

          if (trailingEvent) {
            yield trailingEvent;
          }
        }

        return;
      }

      bufferedChunk += decoder.decode(value, { stream: true });
      const { completedFrames, pendingFrame } = getCompletedFrames(bufferedChunk);
      bufferedChunk = pendingFrame;

      for (const frame of completedFrames) {
        const streamEvent = toStreamEvent(frame);

        if (streamEvent) {
          yield streamEvent;
        }
      }
    }
  } catch (error) {
    if (options.signal.aborted || isAbortError(error)) {
      return;
    }

    throw error;
  } finally {
    options.signal.removeEventListener("abort", cancelReader);
    reader.releaseLock();
  }
}
